import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { formatDuration } from "@offstreetparking/design-system/formatters";
import { Text } from "@offstreetparking/design-system/typography";
import { DateTime } from "luxon";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
const DAYS_PER_WEEK = 7;
const MINUTES_PER_WEEK = MINUTES_PER_DAY * DAYS_PER_WEEK;
export const STEP_SIZE_TYPES = {
	MINUTE: "minute",
	HOUR: "hour",
	DAY: "day",
	WEEK: "week",
} as const;
const getStepSizeUnit = (stepSizeMinutes: number) => {
	if (stepSizeMinutes % MINUTES_PER_WEEK === 0) {
		return {
			unit: STEP_SIZE_TYPES.WEEK,
			count: stepSizeMinutes / MINUTES_PER_WEEK,
		};
	}
	if (stepSizeMinutes % MINUTES_PER_DAY === 0) {
		return {
			unit: STEP_SIZE_TYPES.DAY,
			count: stepSizeMinutes / MINUTES_PER_DAY,
		};
	}
	if (stepSizeMinutes % MINUTES_PER_HOUR === 0) {
		return {
			unit: STEP_SIZE_TYPES.HOUR,
			count: stepSizeMinutes / MINUTES_PER_HOUR,
		};
	}
	return {
		unit: STEP_SIZE_TYPES.MINUTE,
		count: stepSizeMinutes,
	};
};
export const ExpiryTimeHelper = ({
	wizardSettings,
}: {
	wizardSettings: WizardParkingChoice["time"];
}) => {
	const { i18n, t } = useLingui();
	const hasMinDuration = wizardSettings.duration.min !== undefined;
	const hasMaxDuration = wizardSettings.duration.max !== undefined;
	if (wizardSettings.stepSize !== undefined) {
		const { unit, count } = getStepSizeUnit(wizardSettings.stepSize);
		const unitLabel = (() => {
			switch (unit) {
				case "minute":
					return t`minute`;
				case "hour":
					return t`hour`;
				case "day":
					return t`day`;
				case "week":
					return t`week`;
			}
		})();
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{t`${plural(count, {
						one: `Choose a duration in ${count} ${unitLabel} increment`,
						other: `Choose a duration in ${count} ${unitLabel} increments`,
					})}`}
				</Text>
			</div>
		);
	}
	if (hasMaxDuration && hasMinDuration && wizardSettings.duration.min !== wizardSettings.duration.max) {
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{t`Expiry is between ${formatDuration({ duration: wizardSettings.duration.min ?? 0, i18n })} and ${formatDuration({ duration: wizardSettings.duration.max ?? 0, i18n })} after start time`}
				</Text>
			</div>
		);
	}
	if (
		hasMaxDuration &&
		hasMinDuration &&
		wizardSettings.duration.min === wizardSettings.duration.max &&
		wizardSettings.duration.enabled
	) {
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{t`Expiry is ${formatDuration({ duration: wizardSettings.duration.max ?? 0, i18n })} after start time`}
				</Text>
			</div>
		);
	}
	if (hasMaxDuration) {
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{wizardSettings.duration.enabled
						? t`Maximum of ${formatDuration({ duration: wizardSettings.duration.max ?? 0, i18n })} allowed`
						: t`Expires after ${formatDuration({ duration: wizardSettings.duration.max ?? 0, i18n })}`}
				</Text>
			</div>
		);
	}
	if (hasMinDuration) {
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{t`Minimum of ${formatDuration({ duration: wizardSettings.duration.min ?? 0, i18n })} required`}
				</Text>
			</div>
		);
	}
	if (wizardSettings.duration.days !== undefined) {
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{t`${plural(wizardSettings.duration.days, {
						0: "Same day",
						one: "Expiry is # day after the start date",
						other: "Expiry is # days after the start date",
					})}`}
				</Text>
			</div>
		);
	}
	if (wizardSettings.endDate.min && wizardSettings.endDate.max === undefined) {
		const minEndDate = DateTime.fromFormat(wizardSettings.endDate.min, "yyyy-MM-dd").toFormat("LLL d, yyyy", {
			locale: i18n.locale,
		});
		const minEndTime = wizardSettings.endTime.min
			? DateTime.fromFormat(wizardSettings.endTime.min, "HH:mm:ss")
					.minus({
						minutes: 1,
					})
					.toFormat("h:mm a", { locale: i18n.locale })
			: undefined;
		const maxEndTime = wizardSettings.endTime.max
			? DateTime.fromFormat(wizardSettings.endTime.max, "HH:mm:ss").toFormat("h:mm a", { locale: i18n.locale })
			: undefined;
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{wizardSettings.endTime.min
						? t`Must be after ${minEndDate} and after ${minEndTime}`
						: wizardSettings.endTime.max
							? t`Must be after ${minEndDate} and before ${maxEndTime}`
							: t`Must be after ${minEndDate}`}
				</Text>
			</div>
		);
	}
	if (wizardSettings.endDate.max && wizardSettings.endDate.min === undefined) {
		const maxEndDate = DateTime.fromFormat(wizardSettings.endDate.max, "yyyy-MM-dd")
			.plus({
				days: 1,
			})
			.toFormat("LLL d, yyyy", { locale: i18n.locale });
		const maxEndTime = wizardSettings.endTime.max
			? DateTime.fromFormat(wizardSettings.endTime.max, "HH:mm:ss")
					.plus({
						minutes: 1,
					})
					.toFormat("h:mm a", { locale: i18n.locale })
			: undefined;
		const minEndTime = wizardSettings.endTime.min
			? DateTime.fromFormat(wizardSettings.endTime.min, "HH:mm:ss").toFormat("h:mm a", { locale: i18n.locale })
			: undefined;
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">
					{wizardSettings.endTime.max
						? t`Must be before ${maxEndTime} on ${DateTime.fromFormat(wizardSettings.endDate.max, "yyyy-MM-dd").toFormat("LLL d, yyyy", { locale: i18n.locale })}`
						: wizardSettings.endTime.min
							? t`Must be before ${maxEndDate} and after ${minEndTime}`
							: t`Must be before ${maxEndDate}`}
				</Text>
			</div>
		);
	}
	if (wizardSettings.endTime.min && wizardSettings.endTime.max === undefined) {
		const minEndTime = DateTime.fromFormat(wizardSettings.endTime.min, "HH:mm:ss")
			.minus({
				minutes: 1,
			})
			.toFormat("h:mm a", { locale: i18n.locale });
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">{t`Must be after ${minEndTime}`}</Text>
			</div>
		);
	}
	if (wizardSettings.endTime.max && wizardSettings.endTime.min === undefined) {
		const maxEndTime = DateTime.fromFormat(wizardSettings.endTime.max, "HH:mm:ss")
			.plus({
				minutes: 1,
			})
			.toFormat("h:mm a", { locale: i18n.locale });
		return (
			<div className="mb-4 rounded-lg bg-neutral-faded p-2">
				<Text size="sm" weight="medium">{t`Must be before ${maxEndTime}`}</Text>
			</div>
		);
	}
};
